import { useEffect, useState } from 'react';

/**
 * Whether an element is on screen right now (docs/5 § D260805-2116).
 *
 * WHY THIS EXISTS AT ALL: a GIF plays by itself while it is in the viewport and stops
 * when it leaves. "While it is on screen" is the whole of the restraint left in that
 * rule, so it has to be a fact about the element rather than a guess from scroll
 * position — one `IntersectionObserver` per tile is what the platform gives us and it
 * costs nothing when nothing moves.
 *
 * IT IS A BOOLEAN AND NOT A CALLBACK, which is the difference from the observer in
 * `More.tsx`. That one wants to be told *once* when the sentinel arrives, so it fetches
 * a page; this one is a state a render reads, and it has to flip back to `false` on the
 * way out or a feed scrolled to the bottom would be a feed of everything playing at
 * once. The two shapes do not usefully collapse into one hook.
 *
 * WITH NO `IntersectionObserver` IT REPORTS VISIBLE. An old WebView that cannot observe
 * is not a reason to show a reader a frozen tile with no way to start it — the failure
 * mode of the honest answer is a GIF that plays while scrolled past, and the failure
 * mode of the cautious one is an attachment nobody can ever see move.
 */
export function useInViewport<T extends Element>(): {
  ref: (node: T | null) => void;
  visible: boolean;
} {
  /*
   * A CALLBACK REF HELD IN STATE, NOT A `useRef`, and that is a bug fix rather than a
   * style. A ref object is populated *after* the effect that would read it, so an element
   * that mounts later than its component never gets observed at all — which is exactly
   * the composer's attachment preview: the div appears only once a GIF has been picked,
   * seconds after `PostComposer` mounted. It rendered its still frame forever and the
   * observer never saw it. Keying the effect on the element makes "when it appears" the
   * trigger, which is the only correct answer for a conditionally rendered target.
   */
  const [el, setEl] = useState<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!el) {
      /* Unmounted — a removed attachment must not leave a stale `true` behind. */
      setVisible(false);
      return;
    }

    if (typeof IntersectionObserver !== 'function') {
      setVisible(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) setVisible(entry.isIntersecting);
      },
      /*
       * A MARGIN, SO THE GIF IS ALREADY MOVING WHEN IT ARRIVES rather than starting a
       * beat late in the corner of somebody's eye — which is the thing that actually
       * reads as a stutter. It is deliberately SMALL: the animated encoding is a few
       * hundred kilobytes (§ D260805-1521), so a generous margin is a generous
       * prefetch of bytes on somebody's phone data, and the whole reason a reader gets
       * a poster first is that we are not spending those bytes until they are wanted.
       */
      { rootMargin: '96px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [el]);

  return { ref: setEl, visible };
}
